/**
 * Receipt Image Cache
 *
 * Caches rendered receipt images (blob URLs) in memory to avoid re-rendering
 * the same receipt multiple times. This significantly speeds up subsequent views.
 *
 * Cache is cleared when:
 * - Receipt settings are updated (via clearCache)
 * - Page is refreshed (memory cache is lost)
 * - Blob URLs are revoked manually
 */

// In-memory cache for rendered receipt images
// Key: saleId, Value: { blobUrl, timestamp }
const receiptImageCache = new Map();

// Cache TTL: 10 minutes (receipts don't change often)
const CACHE_TTL = 10 * 60 * 1000;

/**
 * Get cached receipt image by sale ID
 * @param {number} saleId - The sale ID
 * @returns {string|null} - Blob URL if cached and valid, null otherwise
 */
export function getCachedReceiptImage(saleId) {
  const cached = receiptImageCache.get(String(saleId));

  if (!cached) {
    return null;
  }

  // Check if cache is expired
  const age = Date.now() - cached.timestamp;
  if (age > CACHE_TTL) {
    // Clean up expired blob URL
    if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(cached.blobUrl);
    }
    receiptImageCache.delete(String(saleId));
    return null;
  }

  return cached.blobUrl;
}

/**
 * Cache a rendered receipt image
 * @param {number} saleId - The sale ID
 * @param {string} blobUrl - The blob URL of the rendered image
 */
export function cacheReceiptImage(saleId, blobUrl) {
  // Clean up old entry if exists
  const old = receiptImageCache.get(String(saleId));
  if (old && old.blobUrl && old.blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(old.blobUrl);
  }

  receiptImageCache.set(String(saleId), {
    blobUrl,
    timestamp: Date.now()
  });

  console.log(`[CACHE] Receipt ${saleId} cached. Total cached: ${receiptImageCache.size}`);
}

/**
 * Remove a specific receipt from cache
 * @param {number} saleId - The sale ID to remove
 */
export function removeCachedReceipt(saleId) {
  const cached = receiptImageCache.get(String(saleId));
  if (cached && cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(cached.blobUrl);
  }
  receiptImageCache.delete(String(saleId));
}

/**
 * Clear all cached receipt images
 * Call this when receipt settings are updated
 */
export function clearReceiptCache() {
  console.log(`[CACHE] Clearing ${receiptImageCache.size} cached receipts`);

  // Clean up all blob URLs
  receiptImageCache.forEach(cached => {
    if (cached.blobUrl && cached.blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(cached.blobUrl);
    }
  });

  receiptImageCache.clear();
}

/**
 * Get cache statistics
 * @returns {object} - Cache stats
 */
export function getCacheStats() {
  return {
    size: receiptImageCache.size,
    entries: Array.from(receiptImageCache.keys())
  };
}
